import useFetchFilteredViewData from './useFetchFilteredViewData';
import ViewSelector, { FilteredViewOption } from './ViewSelector';
import ViewActions from './ViewActions';
import useViewPersistence from './useViewPersistence';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { setCustomFilterDrawer, resetColumnCustomization, setColumnCustomizationState } from '../../store/investor/action';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import { getPlatformUserId, isAppliedFiltersEmpty } from '../../utils/utils';
import { selectColumnCustomization } from '../../store/investor/selectors';
import { getDefaultColumnIds } from '../_custom/DataTable/utils/columnTransformUtils';
import styles from './FilteredViewsButton.module.scss';
import { isEqual } from 'lodash';

enum Source {
    LEADS = 'LEADS',
    DEAL_VIEW = 'DEAL_VIEW',
    MANDATE_VIEW = 'MANDATE_VIEW',
}

type FilteredViewsButtonProps = {
    source: string;
    onViewSelect?: (filters: Record<string, any>, columns?: string[]) => void;
    appliedFilters?: any;
    currentColumns?: string[];
    masterColumnConfig?: any[]; // Master config for resetting columns to base configuration
};

const FilteredViewsButton = (props: FilteredViewsButtonProps) => {
    const { source, onViewSelect, appliedFilters = {}, currentColumns = [], masterColumnConfig = [] } = props;
    const dispatch = useDispatch();
    const [selectedView, setSelectedView] = useState<FilteredViewOption | null>(null);
    const [isViewModified, setIsViewModified] = useState(false);
    const { filteredViews, isLoading, error, filterMapMemo, refetch } = useFetchFilteredViewData(source);

    const userData = useAppSelector((state) => state.user.userData);
    const organizationId = userData?.investorOrgId;
    const userId = getPlatformUserId();
    const columnCustomization = useAppSelector(selectColumnCustomization);

    // Calculate sourceKey once
    const sourceKey = useMemo(() => {
        const sourceMapping: Record<Source, string> = {
            [Source.LEADS]: 'LEADS_PAGE',
            [Source.DEAL_VIEW]: 'DEALS',
            [Source.MANDATE_VIEW]: 'DEALS',
        };
        return sourceMapping[source as Source] || 'DEALS';
    }, [source]);

    // Memoized utility functions
    const countAppliedFilters = useCallback((filters: any): number => {
        let count = 0;
        if (filters.companyStatus?.length > 0) {
            count += filters.companyStatus.length;
        }
        if (filters.customFields) {
            count += Object.keys(filters.customFields).length;
        }
        return count;
    }, []);

    // Check if columns have been customized (different from default state)
    const areColumnsCustomized = useMemo(() => {
        // Columns are considered customized if:
        // 1. currentColumns array has items (indicating column selection has happened)
        // 2. This is a simple heuristic - in practice, any time the user interacts with column customization,
        //    it means they've made changes that should trigger save/update buttons
        return currentColumns.length > 0;
    }, [currentColumns]);

    // Memoized computed values
    const shouldShowSaveButton = useMemo(() => {
        // Show save button if:
        // 1. No view selected AND (filters are applied OR columns are customized)
        // 2. OR if a view has been modified
        const hasChangesWithoutView = !selectedView && (!isAppliedFiltersEmpty(appliedFilters) || areColumnsCustomized);
        return hasChangesWithoutView || isViewModified;
    }, [selectedView, appliedFilters, areColumnsCustomized, isViewModified]);

    const existingViewNames = useMemo(() => filteredViews.map((view) => view.label), [filteredViews]);

    const applyViewFilters = useCallback(
        (filters: any, columns?: string[]) => {
            dispatch(
                setCustomFilterDrawer({
                    open: false,
                    appliedFilters: {
                        [sourceKey]: filters,
                    },
                }),
            );

            // Notify parent component about filter and column changes
            if (onViewSelect) {
                onViewSelect(filters, columns);
            }
        },
        [dispatch, sourceKey, onViewSelect],
    );

    // Initialize persistence hook with modified state tracking
    const { saveViewToStorage, updateURLWithView, clearSelectedView } = useViewPersistence({
        source,
        organizationId,
        filteredViews,
        filterMapMemo,
        onFiltersApply: applyViewFilters,
        onViewSelect: setSelectedView,
        isViewModified,
        setIsViewModified,
    });

    // Clear selected view when source changes (switching between MANDATE_VIEW and DEAL_VIEW)
    useEffect(() => {
        // Don't clear view state when switching between DEAL_VIEW and MANDATE_VIEW
        // as these should share the same view state (columns, filters, etc.)
        if (source === 'DEAL_VIEW' || source === 'MANDATE_VIEW') {
            // Keep the current view state when switching between deal and mandate views
            return;
        }

        setSelectedView(null);
        setIsViewModified(false);

        // Clear the URL viewId parameter when switching sources to prevent
        // the old viewId from being restored in the new source
        const currentParams = new URLSearchParams(window.location.search);
        if (currentParams.has('viewId')) {
            currentParams.delete('viewId');
            window.history.replaceState({}, '', `${window.location.pathname}?${currentParams.toString()}`);
        }
    }, [source]);

    // Mark view as modified when filters OR columns change after a view is selected
    useEffect(() => {
        if (selectedView && filterMapMemo.has(selectedView.value)) {
            const savedView = filteredViews.find((view) => view.value === selectedView.value);
            const savedFilters = filterMapMemo.get(selectedView.value)?.filters || {};
            const savedColumns = savedView?.columns || [];

            const filtersChanged = !isEqual(appliedFilters, savedFilters);
            const columnsChanged = !isEqual(currentColumns, savedColumns);
            const hasChanges = filtersChanged || columnsChanged;

            setIsViewModified(hasChanges);
        } else {
            // Reset modification state when no view is selected
            setIsViewModified(false);
        }
    }, [appliedFilters, currentColumns, selectedView, filterMapMemo, filteredViews]);

    // Expose method to mark view as modified (used by filter drawer and column customization)
    useEffect(() => {
        (window as any).markViewAsModified = () => {
            setIsViewModified(true);
        };

        return () => {
            delete (window as any).markViewAsModified;
        };
    }, []);

    const handleViewSelect = useCallback(
        (option: FilteredViewOption | null) => {
            setSelectedView(option);
            setIsViewModified(false); // Reset modified state when selecting a new view

            if (option?.value) {
                const filterData = filterMapMemo.get(option.value);
                if (filterData) {
                    applyViewFilters(filterData.filters);
                    saveViewToStorage(option.value, option.label);
                    updateURLWithView(option.value);

                    // Update columns based on the selected view
                    if (filterData.columns && filterData.columns.length > 0) {
                        // Use the columns from the selected view
                        dispatch(
                            resetColumnCustomization({
                                defaultColumns: filterData.columns,
                            }),
                        );

                        if (onViewSelect) {
                            onViewSelect(filterData.filters, filterData.columns);
                        }
                    } else {
                        // Clear column customization state entirely (as if clicking cross button)
                        dispatch(
                            setColumnCustomizationState({
                                open: false,
                                sourceKey: sourceKey,
                                selectedColumns: [],
                                tempSelectedColumns: [],
                                isModified: false,
                            }),
                        );

                        if (onViewSelect) {
                            onViewSelect(filterData.filters, []);
                        }
                    }
                }
            } else {
                // Clear view selection (mark as explicitly cleared)
                applyViewFilters({});
                saveViewToStorage(null, null, true);
                updateURLWithView(null);

                // Clear column customization state entirely when cross icon is clicked
                dispatch(
                    setColumnCustomizationState({
                        open: false,
                        sourceKey: sourceKey,
                        selectedColumns: [],
                        tempSelectedColumns: [],
                        isModified: false,
                    }),
                );

                // Notify parent component about column reset
                if (onViewSelect) {
                    onViewSelect({}, []);
                }
            }
        },
        [filterMapMemo, applyViewFilters, saveViewToStorage, updateURLWithView, onViewSelect, sourceKey, masterColumnConfig, dispatch],
    );

    // Handle view saved callback
    const handleViewSaved = useCallback(
        (viewId: string, viewName: string) => {
            const newViewOption = { label: viewName, value: viewId };
            setSelectedView(newViewOption);
            setIsViewModified(false); // Reset modified state after saving
            saveViewToStorage(viewId, viewName);
            updateURLWithView(viewId);
        },
        [saveViewToStorage, updateURLWithView],
    );

    // Listen for view updates from SaveViewButton
    useEffect(() => {
        const handleViewUpdate = (event: CustomEvent) => {
            if (event.detail?.source === source) {
                refetch?.();
            }
        };

        window.addEventListener('viewSaved' as any, handleViewUpdate);
        return () => {
            window.removeEventListener('viewSaved' as any, handleViewUpdate);
        };
    }, [source, refetch]);

    // Expose clearSelectedView to external components via window object for clearing filters
    useEffect(() => {
        (window as any).clearFilteredView = () => {
            clearSelectedView();
            setSelectedView(null);
            setIsViewModified(false);
        };
        return () => {
            delete (window as any).clearFilteredView;
        };
    }, [clearSelectedView]);

    return (
        <div className={styles.FilteredViewsContainer}>
            <ViewSelector
                options={filteredViews}
                selectedView={selectedView}
                onViewSelect={handleViewSelect}
                isLoading={isLoading}
                error={error}
                userData={userData}
                organizationId={organizationId}
                source={source}
                countAppliedFilters={countAppliedFilters}
                filterMapMemo={filterMapMemo}
            />

            <ViewActions
                selectedView={selectedView}
                appliedFilters={appliedFilters}
                userData={userData}
                organizationId={organizationId}
                userId={userId}
                source={source}
                existingViewNames={existingViewNames}
                countAppliedFilters={countAppliedFilters}
                filterMapMemo={filterMapMemo}
                onViewSaved={handleViewSaved}
                onRefetch={refetch}
                shouldShowSaveButton={shouldShowSaveButton}
                currentColumns={currentColumns}
            />
        </div>
    );
};

export default FilteredViewsButton;
